import { Map, Record as ImmutableRecord } from 'immutable';
import forOwn from 'lodash/forOwn';
import uniqueId from 'lodash/uniqueId';
import omit from 'lodash/omit';
import invariant from 'invariant';
import SyncHook from '../SyncHook';
const RowModelRecord = ImmutableRecord({
    id: null,
    _destroy: false,
    regvars: Map(),
    vdlists: Map(),
    subTables: Map(),
    $id: null,
    $extra: null,
    $rcc: null,
    $validationGroups: Map(),
    $temp: null
}, 'RowModel');
function defaultValidateShortname(shortname) {
    const $rcc = this.$rcc;
    if (!$rcc.tableMetadata.regvars[shortname]) {
        throw new Error(`Tabellen ${$rcc.tableName} har ingen registervariabel med kortnamnet ${shortname}.`);
    }
}
function defaultValidateTableName(tableName) {
    const $rcc = this.$rcc;
    const tableMetadata = $rcc.tableMetadata;
    if (tableMetadata.subTables.indexOf(tableName) === -1) {
        throw new Error(`${tableName} is not a subtable to ${$rcc.tableName}.`);
    }
}
function defaultValidateVdlist(name) {
    const $rcc = this.$rcc;
    if (!$rcc.tableMetadata.vdlists[name]) {
        throw new Error(`Tabellen ${$rcc.tableName} har ingen värdedomän med namnet ${name}.`);
    }
}
const staticHooks = {
    validateRegvarShortname: new SyncHook(),
    validateSubTableName: new SyncHook(),
    validateVdlistName: new SyncHook()
};
staticHooks.validateRegvarShortname.add(defaultValidateShortname);
staticHooks.validateSubTableName.add(defaultValidateTableName);
staticHooks.validateVdlistName.add(defaultValidateVdlist);
export class RowModel extends RowModelRecord {
    static hooks = staticHooks;
    constructor(values){
        values ? super(values) : super();
        invariant(values?.$rcc, '$rcc must be initialized.');
    }
    getId() {
        if (this.id) {
            return this.id;
        }
        if (this.$id) {
            return this.$id;
        }
        throw new Error('The row is missing both id and $id.');
    }
    getSubTable(tableName) {
        staticHooks.validateSubTableName.exec(this, tableName);
        return this.getIn([
            'subTables',
            tableName
        ]);
    }
    getSubTableRows(tableName) {
        return this.getSubTable(tableName).getRows();
    }
    addSubTableRow(row) {
        if (!row) {
            throw new Error('Cannot add an empty row!');
        }
        const rowTableName = row.get('$rcc').tableName;
        const tableMetadata = this.get('$rcc').tableMetadata;
        const tableName = this.get('$rcc').tableName;
        if (tableMetadata.subTables.indexOf(rowTableName) === -1) {
            throw new Error(`Cannot add the row because the table ${rowTableName} is not a subtable to ${tableName}.`);
        }
        return this.updateIn([
            'subTables',
            rowTableName
        ], (subTable)=>{
            return subTable.addRow(row);
        });
    }
    removeSubTableRow(row) {
        const rowTableName = row.$rcc.tableName;
        const tableMetadata = this.$rcc.tableMetadata;
        const tableName = this.$rcc.tableName;
        if (tableMetadata.subTables.indexOf(rowTableName) === -1) {
            throw new Error(`Cannot remove the row because the table ${rowTableName} is not a subtable to ${tableName}.`);
        }
        return this.updateIn([
            'subTables',
            rowTableName
        ], (subTable)=>{
            return subTable.removeRow(row.getId());
        });
    }
    updateSubTableRow(subTableName, rowId, updater) {
        staticHooks.validateSubTableName.exec(this, subTableName);
        return this.updateIn([
            'subTables',
            subTableName
        ], (subTable)=>{
            return subTable.updateRow(rowId, updater);
        });
    }
    updateSubTableRows(subTableName, predicate, updater) {
        const rowsToUpdate = this.getSubTableRows(subTableName).filter((x)=>predicate(x));
        return rowsToUpdate.reduce((reduction, row)=>{
            return reduction.updateSubTableRow(subTableName, row.getId(), updater);
        }, this);
    }
    updateRegvar(shortname, updater) {
        return this.updateIn([
            'regvars',
            shortname
        ], updater);
    }
    removeSubTableRows(subTableName, predicate) {
        staticHooks.validateSubTableName.exec(this, subTableName);
        return this.updateIn([
            'subTables',
            subTableName
        ], (subTable2)=>{
            const subTable = subTable2;
            return subTable.getRows().filter((x)=>predicate(x)).reduce((reduction, subTableRow)=>{
                return reduction.removeRow(subTableRow.getId());
            }, subTable);
        });
    }
    clearSubTable(tableName) {
        const rows = this.getSubTableRows(tableName);
        return rows.reduce((result, subtableRow)=>{
            return result.removeSubTableRow(subtableRow);
        }, this);
    }
    getRegvar(shortname) {
        staticHooks.validateRegvarShortname.exec(this, shortname);
        return this.getIn([
            'regvars',
            shortname
        ]);
    }
    getRegvarInclude(shortname) {
        return this.getRegvar(shortname).include;
    }
    setRegvarInclude(shortname, value, options) {
        staticHooks.validateRegvarShortname.exec(this, shortname);
        return this.updateIn([
            'regvars',
            shortname
        ], (rv)=>rv.setInclude(value, options));
    }
    getRegvarValue(shortname) {
        return this.getRegvar(shortname).getValue();
    }
    setRegvarValue(shortname, value, options) {
        staticHooks.validateRegvarShortname.exec(this, shortname);
        return this.updateIn([
            'regvars',
            shortname
        ], (rv2)=>{
            const rv = rv2;
            return rv.setValue(value, options);
        });
    }
    getRegvarListItemByValue(shortname, value) {
        staticHooks.validateRegvarShortname.exec(this, shortname);
        const regvar = this.getRegvar(shortname);
        if (regvar.$rcc.regvarMetadata.dataType !== 'list') {
            throw new Error(`Cannot get listitems, because regvar ${shortname.toString()} is not of type list.`);
        }
        return this.getRegvar(shortname).getListItemByValue(value);
    }
    getRegvarListItemById(shortname, value) {
        staticHooks.validateRegvarShortname.exec(this, shortname);
        const regvar = this.getRegvar(shortname);
        if (regvar.$rcc.regvarMetadata.dataType !== 'list') {
            throw new Error(`Cannot get listitems, because regvar ${shortname.toString()} is not of type list.`);
        }
        return this.getRegvar(shortname).getListItemById(value);
    }
    getRegvarListItems(shortname, filterFn) {
        staticHooks.validateRegvarShortname.exec(this, shortname);
        const regvar = this.getRegvar(shortname);
        if (regvar.$rcc.regvarMetadata.dataType !== 'list') {
            throw new Error(`Cannot get listitems, because regvar ${shortname.toString()} is not of type list.`);
        }
        return this.getRegvar(shortname).getListItems(filterFn);
    }
    isRegvarOneOf(shortname, values) {
        staticHooks.validateRegvarShortname.exec(this, shortname);
        const regvar = this.getRegvar(shortname);
        if (regvar.$rcc.regvarMetadata.dataType !== 'list') {
            throw new Error(`Cannot do check, because regvar ${shortname.toString()} is not of type list.`);
        }
        return this.getRegvar(shortname).isOneOf(values);
    }
    markInvalidEntriesAsDirty(options = {}) {
        const tableMetadata = this.$rcc.tableMetadata;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let result = this;
        if (!options.validationPredicate || options.validationPredicate(this)) {
            forOwn(tableMetadata.regvars, (value, key)=>{
                result = result.updateIn([
                    'regvars',
                    key
                ], (rv2)=>{
                    const rv = rv2;
                    if (rv.$errors.isEmpty()) {
                        return rv;
                    }
                    return rv.set('$isDirty', true);
                });
            });
            this.$validationGroups.forEach((group, groupKey)=>{
                group.forEach((validationGroupItem, validationGroupItemKey)=>{
                    if (validationGroupItem.$errors && validationGroupItem.$errors.length) {
                        result = result.updateIn([
                            '$validationGroups',
                            groupKey,
                            validationGroupItemKey
                        ], (item)=>{
                            return {
                                ...item,
                                $isDirty: true
                            };
                        });
                    }
                });
            });
        }
        if (!options.skipSubTables) {
            tableMetadata.subTables.forEach((tableName)=>{
                result = result.updateIn([
                    'subTables',
                    tableName,
                    'items'
                ], (subTableRow)=>{
                    const subTableRows = subTableRow;
                    const validatedRows = subTableRows.map((str)=>str.markInvalidEntriesAsDirty(options));
                    // Only return the new mapped rows if they differ, otherwise make sure we keep
                    // the same immutable instance.
                    if (validatedRows.some((validatedRow, i)=>subTableRows.get(i) !== validatedRow)) {
                        return validatedRows;
                    }
                    return subTableRows;
                });
            });
        }
        return result;
    }
    setRegvarErrors(shortname, errors) {
        staticHooks.validateRegvarShortname.exec(this, shortname);
        return this.updateIn([
            'regvars',
            shortname
        ], (rv)=>{
            return rv.setValidationErrors(errors);
        });
    }
    clearRegvarValue(shortname, options) {
        return this.setRegvarValue(shortname, null, options);
    }
    getVdlist(name) {
        staticHooks.validateVdlistName.exec(this, name);
        return this.getIn([
            'vdlists',
            name
        ]);
    }
    getVdlistValue(name, ...options) {
        return this.getVdlist(name).getValue(...options);
    }
    setVdlistValue(name, value, options) {
        staticHooks.validateVdlistName.exec(this, name);
        return this.updateIn([
            'vdlists',
            name
        ], (rv)=>{
            return rv.setValue(value, options);
        });
    }
    clearVdlistValue(name, options) {
        return this.setVdlistValue(name, null, options);
    }
    /**
     * Gets a list of all validation errors.
     *
     * @param options {Object} - Options object.
     *                           includePristine: even those not marked as dirty - default false
     *                           include: regvars to include - default all
     *                           exclude: regvars to exclude - default none
     */ getErrors(options) {
        const result = [];
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const $rcc = this.$rcc;
        const tableMetadata = $rcc.tableMetadata;
        if (!options?.validationPredicate || options.validationPredicate(this)) {
            forOwn(tableMetadata.regvars, (rv, shortname)=>{
                const regvar = self.getIn([
                    'regvars',
                    shortname
                ]);
                const include = (!options?.include || options.include.indexOf(shortname) !== -1) && (options?.exclude || []).indexOf(shortname) === -1;
                if (include && (regvar.$isDirty || options?.includePristine) && !regvar.$errors.isEmpty()) {
                    result.push(regvar);
                }
            });
            forOwn(tableMetadata.vdlists, (rv, vdlist)=>{
                const item = self.getIn([
                    'vdlists',
                    vdlist
                ]);
                if ((item.$isDirty || options?.includePristine) && !item.$errors.isEmpty()) {
                    result.push(item);
                }
            });
            this.$validationGroups.forEach((group)=>{
                group.forEach((validationGroupItem)=>{
                    if (validationGroupItem.$errors.length && (validationGroupItem.$isDirty || options?.includePristine)) {
                        result.push(validationGroupItem);
                    }
                });
            });
        }
        if (!options?.skipSubTables) {
            tableMetadata.subTables.forEach((subTableName)=>{
                const subTable = this.getSubTable(subTableName);
                result.push(...subTable.getErrors(options));
            });
        }
        return result;
    }
    isValidationGroupDirty(name) {
        return !!this.$validationGroups.get(name)?.some((x)=>x.$isDirty);
    }
    clone() {
        return this.$rcc.tableMetadata.subTables.reduce((reduction, subTableName)=>{
            const subTable = reduction.getSubTable(subTableName);
            return reduction.setIn([
                'subTables',
                subTableName,
                'items'
            ], subTable.getRows()) // No need to keep eventual _destroy'd rows when cloning.
            .updateIn([
                'subTables',
                subTableName,
                'items'
            ], (stRows)=>stRows.map((x)=>x.clone()));
        }, this.set('id', null).set('$id', uniqueId('data_')).update('$extra', (x)=>omit(x, 'documentId', 'errandId', 'registerRecordId')));
    }
    clearData() {
        const withoutSubtableRows = this.$rcc.tableMetadata.subTables.reduce((acc, subTableName)=>{
            return acc.clearSubTable(subTableName);
        }, this);
        const withoutVdlists = Object.keys(this.$rcc.tableMetadata.vdlists).reduce((acc, vdlist)=>{
            return acc.clearVdlistValue(vdlist);
        }, withoutSubtableRows);
        return Object.keys(this.$rcc.tableMetadata.regvars).reduce((acc, regvarShortname)=>{
            return acc.clearRegvarValue(regvarShortname);
        }, withoutVdlists);
    }
    fillData(source, excludedVariables = []) {
        const fillRowRegvars = (dest, sourceRegvars)=>{
            let r = dest;
            const sourceRegvarShortNames = Object.keys(sourceRegvars);
            const regvars = Object.keys(dest.regvars.toJS()).filter((shortname)=>sourceRegvarShortNames.includes(shortname));
            regvars.forEach((shortname)=>{
                const value = excludedVariables.includes(shortname) ? this.getRegvarValue(shortname) : sourceRegvars[shortname].value;
                r = r.setRegvarValue(shortname, value);
            });
            return r;
        };
        const fillForm = (destinationRow, sourceRow)=>{
            let r = destinationRow;
            r = fillRowRegvars(r, sourceRow.regvars);
            const sourceTableNames = Object.keys(sourceRow.subTables);
            const destTables = destinationRow.subTables.map((subTable)=>subTable?.$rcc.tableName);
            const tables = sourceTableNames.filter((sourceTableName)=>destTables.includes(sourceTableName));
            tables.forEach((sourceTableName)=>{
                const subTableItems = Object.values(sourceRow.subTables[sourceTableName]);
                subTableItems.forEach((sourceSubRow)=>{
                    let newRow = destinationRow.getSubTable(sourceTableName).createRow().setIn([
                        '$extra',
                        'registerRecordId'
                    ], sourceSubRow.registerRecordId);
                    newRow = fillForm(newRow, sourceSubRow);
                    r = r.addSubTableRow(newRow);
                });
            });
            return r;
        };
        return fillForm(this, source);
    }
    toIncaJS() {
        const result = {
            ...this.$extra,
            regvars: this.regvars.reduce((reduction, item, key)=>{
                reduction[key] = item.toIncaJS();
                return reduction;
            }, {}),
            vdlists: this.vdlists.reduce((reduction, item, key)=>{
                reduction[key] = item.toIncaJS();
                return reduction;
            }, {}),
            subTables: this.subTables.reduce((reduction, item)=>{
                reduction[item.$rcc.tableName] = item.toIncaJS();
                return reduction;
            }, {})
        };
        if (this.id) {
            result.id = this.id;
        }
        if (typeof this._destroy === 'boolean') {
            result._destroy = this._destroy;
        }
        return result;
    }
}
